/**
 * Server-based sales person API functions.
 * These functions call the general-system server API endpoints with authentication.
 */

import { authenticatedHttpClient } from './httpClient';
import {
  SalesPerson,
  SalesPersonCreate,
  SalesPersonUpdate,
} from '../models/sales_person';
import {
  LIST_PAGE_SIZE,
  SELECT_MAX_ITEMS,
  SELECT_PAGE_SIZE,
} from './listPagination';

export type SalesPeopleSortField = 'created' | 'name' | 'code';

export { LIST_PAGE_SIZE as SALES_PEOPLE_DEFAULT_PAGE_SIZE };

/**
 * Create a new sales person through the server API
 */
export const createSalesPersonServer = async (
  organizationId: string,
  salesPersonData: SalesPersonCreate
): Promise<SalesPerson> => {
  try {
    const response = await authenticatedHttpClient.post<SalesPerson>(
      `/sales-people/${organizationId}`,
      salesPersonData
    );
    return response;
  } catch (error: any) {
    console.error('Error creating sales person:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al crear el vendedor'
    );
  }
};

/**
 * Get all sales people for an organization through the server API
 */
export const getSalesPeopleServer = async (
  organizationId: string,
  options?: {
    skip?: number;
    limit?: number;
    searchTerm?: string;
    sortField?: SalesPeopleSortField;
    sortDirection?: 1 | -1;
  }
): Promise<{ items: SalesPerson[]; hasMore: boolean }> => {
  try {
    const params = new URLSearchParams();
    if (options?.skip != null) {
      params.set('skip', String(options.skip));
    }
    params.set('limit', String(options?.limit ?? LIST_PAGE_SIZE));
    if (options?.searchTerm?.trim()) {
      params.set('search_term', options.searchTerm.trim().toLowerCase());
    }
    if (options?.sortField) {
      params.set('sort_field', options.sortField);
    }
    if (options?.sortDirection != null) {
      params.set('sort_direction', String(options.sortDirection));
    }
    const qs = params.toString();
    const response = await authenticatedHttpClient.get<{
      items: SalesPerson[];
      hasMore: boolean;
    }>(`/sales-people/${organizationId}${qs ? `?${qs}` : ''}`);
    return {
      items: response.items,
      hasMore: response.hasMore,
    };
  } catch (error: any) {
    console.error('Error getting sales people:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al obtener los vendedores'
    );
  }
};

/**
 * Walk paginated sales-people pages for dropdowns (bounded, no single 500-row request).
 */
export const listSalesPeopleForSelect = async (
  organizationId: string,
  options?: {
    search?: string;
    maxItems?: number;
    pageSize?: number;
  }
): Promise<SalesPerson[]> => {
  const pageSize = options?.pageSize ?? SELECT_PAGE_SIZE;
  const maxItems = options?.maxItems ?? SELECT_MAX_ITEMS;
  const searchTerm = options?.search?.trim() || undefined;
  const collected: SalesPerson[] = [];
  let skip = 0;

  while (collected.length < maxItems) {
    const remaining = maxItems - collected.length;
    const limit = Math.min(pageSize, remaining);
    const page = await getSalesPeopleServer(organizationId, {
      skip,
      limit,
      searchTerm,
      sortField: 'name',
      sortDirection: 1,
    });
    collected.push(...page.items);
    if (!page.hasMore || page.items.length === 0) {
      break;
    }
    skip += page.items.length;
  }

  return collected;
};

/**
 * Update a sales person through the server API
 */
export const updateSalesPersonServer = async (
  organizationId: string,
  salesPersonId: string,
  salesPersonData: SalesPersonUpdate
): Promise<SalesPerson> => {
  try {
    const response = await authenticatedHttpClient.put(
      `/sales-people/${organizationId}/${salesPersonId}`,
      salesPersonData
    );
    return response;
  } catch (error: any) {
    console.error('Error updating sales person:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al actualizar el vendedor'
    );
  }
};

/**
 * Get a sales person by ID through the server API
 */
export const getSalesPersonServer = async (
  organizationId: string,
  salesPersonId: string
): Promise<SalesPerson> => {
  try {
    const response = await authenticatedHttpClient.get(
      `/sales-people/${organizationId}/${salesPersonId}`
    );
    return response;
  } catch (error: any) {
    console.error('Error getting sales person:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al obtener el vendedor'
    );
  }
};

/**
 * Delete a sales person through the server API
 */
export const deleteSalesPersonServer = async (
  organizationId: string,
  salesPersonId: string
): Promise<void> => {
  try {
    await authenticatedHttpClient.delete(
      `/sales-people/${organizationId}/${salesPersonId}`
    );
  } catch (error: any) {
    console.error('Error deleting sales person:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al eliminar el vendedor'
    );
  }
};
